import { useEffect, useRef, type AnchorHTMLAttributes, type ReactNode } from "react";
import { Link as RouterLink } from "react-router-dom";
import { preloadTourStopBySlug } from "@/lib/data/repo";

type Href = string | { pathname?: string; query?: Record<string, string | number | undefined> };

type Props = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "href"> & {
  href: Href;
  children?: ReactNode;
  replace?: boolean;
  scroll?: boolean;
  prefetch?: boolean;
};

function hrefValue(href: Href): string {
  if (typeof href === "string") return href;
  const params = new URLSearchParams();
  Object.entries(href.query ?? {}).forEach(([key, value]) => {
    if (value !== undefined) params.set(key, String(value));
  });
  const query = params.toString();
  return `${href.pathname ?? "/"}${query ? `?${query}` : ""}`;
}

function eventSlug(href: string) {
  const match = href.match(/^\/events\/([^/?#]+)\/?(?:[?#].*)?$/);
  return match ? decodeURIComponent(match[1]) : undefined;
}

function preloadEvent(href: string) {
  const slug = eventSlug(href);
  if (!slug) return;
  void preloadTourStopBySlug(slug).catch(() => undefined);
}

export default function Link({ href, replace, scroll: _scroll, prefetch = true, onMouseEnter, onFocus, onTouchStart, ...props }: Props) {
  const value = hrefValue(href);
  const anchorRef = useRef<HTMLAnchorElement>(null);

  useEffect(() => {
    if (!prefetch || !eventSlug(value) || !anchorRef.current || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        preloadEvent(value);
        observer.disconnect();
      }
    }, { rootMargin: "300px" });
    observer.observe(anchorRef.current);
    return () => observer.disconnect();
  }, [prefetch, value]);

  return <RouterLink
    ref={anchorRef}
    to={value}
    replace={replace}
    onMouseEnter={(event) => { preloadEvent(value); onMouseEnter?.(event); }}
    onFocus={(event) => { preloadEvent(value); onFocus?.(event); }}
    onTouchStart={(event) => { preloadEvent(value); onTouchStart?.(event); }}
    {...props}
  />;
}
